import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { FeatureFlags } from '../../FeatureFlags';
import { L10n } from '../../L10n/L10n';
import {
  DatabaseContentType,
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  MetricContentType,
  ProjectContentType,
  PublishedConnectionContentType,
  TableContentType,
  ViewContentType,
  WorkbookContentType,
} from '../../models/ContentTypes';
import { IProjectInfo } from '../../models/IProjectInfo';
import { IWorkbookInfo } from '../../models/IWorkbookInfo';
import { IControlledPermissionItemInfo, INamedItemInfo } from '../../models/resourceInfoParts';
import { Set } from '../../models/Set';
import { ITypedIdWithName } from '../../ServerApi/types/ITypedId';
import { LockStatusHelper, LockUnlockPermissionsState } from '../LockStatusHelper';

export interface PermissionsEditability {

  /**
   * Can the permissions of the given authorizables be edited?
   * If some can and some can't, the overall editability is false.
   */
  editable: boolean;

  /**
   * If the authorizables are of type that can be locked (e.g. top-level projects and databases),
   * then lockState indicates the locked state of the authorizables.
   * Otherwise, it indicates whether the authorizables (e.g. workbooks, data sources, child projects, etc.)
   * are locked to their containing top-level project or database.
   * Note that this notion of "locked" doesn't include the notion of "show sheets as tabs" for workbooks;
   * it only includes explicitly locking top-level projects or databases.
   */
  lockState: LockUnlockPermissionsState;

  /**
   * Shown at the top-left of the permissions dialog.
   * Describes the set of authorizables and whether they are editable.
   * Example: Edit permissions for workbook "Superstore"
   */
  prompt: string;

  /**
   * For authorizables that can't be locked (e.g. workbooks), shown in the top-right of the permissions dialog.
   * Indicates whether the authorizables are in a locked container or not.
   * Apparently unused by lockable containers (top-level projects and databases),
   * which source the strings for that location elsewhere.
   */
  shortMessage: string;

  /**
   * Apparently unused.
   */
  longMessage: string;

  /**
   * Shown in each template section header for top-level projects and databases.
   * Indicates whether permissions for items of that template type are
   * locked to the container or managed by the owner.
   */
  sectionHeaderMessage: string | null;
}

export interface PermissionsEditabilityLogic {
  getEditabilityWithMessages: () => PermissionsEditability;
}

export abstract class PermissionsEditabilityLogicBase implements PermissionsEditabilityLogic {

  protected editable: boolean;

  constructor(protected authorizables: Set<INamedItemInfo>,
              protected authorizableType: string,
              protected sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
              protected $translate: ng.translate.ITranslateService) {
    this.editable = this.allAuthorizablesMatchSource();
  }

  public getEditabilityWithMessages(): PermissionsEditability {
    const messages = this.getMessages();
    const lockState = this.getLockState();
    return {
      editable: this.editable,
      lockState: lockState,
      prompt: this.getPrompt(),
      shortMessage: messages[0],
      longMessage: messages[1],
      sectionHeaderMessage: this.getSectionHeaderMessageForLockState(lockState),
    };
  }

  // An authorizable object is one that can have permissions set on it. An authorizable object
  // always has a source authorizable object attached. If the object is in control of its own
  // permissions, it will be its own source. If the object is controlled by another object
  // (e.g it is a workbook inside a locked project) then the source authorizable will be the
  // controlling object.
  // So permissions of object A are editable if and only if A matches sourceAuthorizables[A.id]
  protected allAuthorizablesMatchSource(): boolean {
    return this.authorizables.all((authorizable: INamedItemInfo) => {
      const source = this.sourceAuthorizables[authorizable.id];
      return this.authorizableType === source.type && authorizable.id === source.id;
    });
  }

  protected noAuthorizablesMatchSource(): boolean {
    return this.authorizables.all((authorizable: INamedItemInfo) => {
      const source = this.sourceAuthorizables[authorizable.id];
      return this.authorizableType !== source.type || authorizable.id !== source.id;
    });
  }

  protected getLockState(): LockUnlockPermissionsState {
    if (this.editable) {
      return LockUnlockPermissionsState.Unlocked;
    } else {
      if (this.noAuthorizablesMatchSource()) {
        return LockUnlockPermissionsState.Locked;
      } else {
        return LockUnlockPermissionsState.Mixed;
      }
    }
  }

  protected getSectionHeaderMessageForLockState(_lockState: LockUnlockPermissionsState): string | null {
    return null;
  }

  protected abstract getPromptId(): string;
  protected abstract getMessages(): string[];

  private getPrompt(): string {
    if (FeatureFlags.permissionsDialogReactEnabled) {
      return '';
    }
    return this.$translate.instant(this.getPromptId(),
        {
          ITEM_NAME: L10n.elideIfLong(this.authorizables.first().name),
          ITEM_COUNT: this.authorizables.size()
        },
        L10n.Interpolations.Count
      ) || '';
  }

}


export class ProjectPermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<IProjectInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, ProjectContentType.name, sourceAuthorizables, $translate);
    this.authorizablesIncludeChildProject = authorizables.any((project: IProjectInfo) => !project.topLevelProject);
    this.authorizablesIncludeLockedChildProject =
      authorizables.any((project: IProjectInfo) => {
        if (FeatureFlags.lockingSubProjectsEnabled) {
          // a project is controlled by another project if it is not a top level project and its source authorizable is not itself
          return !project.topLevelProject && sourceAuthorizables[project.id].id !== project.id;
        }
        return (!project.topLevelProject && project.controlledPermissionsEnabled);
      });
    // we have to override the default value for editability because child projects don't work in
    // the expected way with source authorizables with locking subprojects disabled
    if (!FeatureFlags.lockingSubProjectsEnabled) {
      this.editable = this.editable && !this.authorizablesIncludeLockedChildProject;
    }
  }

  private authorizablesIncludeChildProject: boolean;

  /** Used to determine if permissions are editable and what the lockstate is */
  private authorizablesIncludeLockedChildProject: boolean;

  protected getLockState(): LockUnlockPermissionsState {
    if (this.authorizablesIncludeLockedChildProject) {
      const allAuthorizablesAreLockedChildProjects =
        this.authorizables.all((project: IProjectInfo) =>
          (!project.topLevelProject && project.controlledPermissionsEnabled));
      if (allAuthorizablesAreLockedChildProjects) {
        return LockUnlockPermissionsState.Locked;
      } else {
        return LockUnlockPermissionsState.Mixed;
      }
    } else {
      const projects = this.authorizables as Set<IProjectInfo>;
      return LockStatusHelper.getLockState(projects);
    }
  }

  protected getSectionHeaderMessageForLockState(lockState: LockUnlockPermissionsState): string | null {
    switch (lockState) {
      case LockUnlockPermissionsState.Locked:
        return Messages.lockProjectPermissions_locked();
      case LockUnlockPermissionsState.Unlocked:
        return Messages.lockProjectPermissions_unlocked();
      case LockUnlockPermissionsState.Mixed:
        return Messages.lockProjectPermissions_mixed();
    }
  }

  protected getPromptId(): string {
    // BUG: Since child projects permissions can be viewed even when they are not editable
    // (if they're in a locked top-level project), there should be a prompt for the non-editable case.
    // (jraymakers discovered this past the loc deadline for 2019.3)
    return this.editable ? 'editProjectPermissionsDescription': '';
  }

  protected getMessages(): string[] {
    const stringIds = this.authorizablesIncludeChildProject
      ? this.getMessageIdsForChild()
      : ['', ''];
    return stringIds.map((stringId: string) => this.$translate.instant(stringId));
  }

  private getMessageIdsForChild(): string[] {
    let shortMessageId, longMessageId;
    if (this.authorizablesIncludeLockedChildProject) {
      shortMessageId = 'lockProjectPermissions_inheritedLocked';
      longMessageId = 'lockProjectPermissions_inheritedLocked';
    } else {
      shortMessageId = 'lockProjectPermissions_inheritedUnlocked';
      longMessageId = 'lockProjectPermissions_inheritedUnlocked';
    }
    return [shortMessageId, longMessageId];
  }

}

export enum WorkbookSheetsAsTabsState {
  DisplayTabs,
  IndependentTabs,
  Mixed,
}

export class WorkbookPermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, WorkbookContentType.name, sourceAuthorizables, $translate);
  }

  protected getPromptId(): string {
    return this.editable ? 'editWorkbookPermissionsDescription': 'readonlyWorkbookPermissionsDescription';
  }

  protected getMessages(): string[] {
    if (FeatureFlags.lockingSubProjectsEnabled) {
      switch (this.getLockState()) {
        case LockUnlockPermissionsState.Locked:
          return [Messages.lockPermissions_contentInLockedProject(), Messages.lockPermissions_contentInLockedProject()];
        case LockUnlockPermissionsState.Unlocked:
          switch (this.getSheetsAsTabs()) {
            case WorkbookSheetsAsTabsState.DisplayTabs:
              return [Messages.lockPermissions_workbookViewsDisplayTabs(), Messages.lockPermissions_workbookViewsDisplayTabs()];
            case WorkbookSheetsAsTabsState.IndependentTabs:
              return [Messages.lockPermissions_workbookViewsIndependentTabs(), Messages.lockPermissions_workbookViewsIndependentTabs()];
            case WorkbookSheetsAsTabsState.Mixed:
              return [Messages.lockPermissions_workbookViewsMixedTabs(), Messages.lockPermissions_workbookViewsMixedTabs()];
          }
          break;
        case LockUnlockPermissionsState.Mixed:
          return [Messages.lockPermissions_contentInMixedProject(), Messages.lockPermissions_contentInMixedProject()];
      }
    }
    const stringIds = this.editable ? this.messageIdsForEditable() : this.messageIdsForReadOnly();
    return stringIds.map((stringId: string) => this.$translate.instant(
      stringId,
      {
        ITEM_COUNT: this.authorizables.size(),
        // SOURCE_NAME is only used in the string when there's a single locked workbook,
        // so we can safely take the first source without knowing what type it is
        SOURCE_NAME: L10n.elideIfLong(this.sourceAuthorizables[this.authorizables.first().id].name)
      },
      L10n.Interpolations.Count
    ));
  }

  private getSheetsAsTabs(): WorkbookSheetsAsTabsState {
    const workbooks = <Set<IWorkbookInfo>>this.authorizables;
    const displayTabs = workbooks.first().displayTabs;
    const allTheSame = workbooks.all((workbook: IWorkbookInfo) => workbook.displayTabs === displayTabs);
    if (!allTheSame) {
      return WorkbookSheetsAsTabsState.Mixed;
    } else if (displayTabs) {
      return WorkbookSheetsAsTabsState.DisplayTabs;
    } else {
      return WorkbookSheetsAsTabsState.IndependentTabs;
    }
  }

  private messageIdsForEditable(): string[] {
    const workbooks = <Set<IWorkbookInfo>>this.authorizables;
    const displayTabs = workbooks.first().displayTabs;
    const allTheSame = workbooks.all((workbook: IWorkbookInfo) => workbook.displayTabs === displayTabs);
    const singleWorkbook = workbooks.size() === 1;

    let shortMessageId, longMessageId;
    if (!allTheSame) {
      shortMessageId = 'permissionsShortMessage_workbooksSomeWithIndependentViews';
      longMessageId = 'permissionsLongMessage_workbooksSomeWithIndependentViews';
    } else if (displayTabs) {
      shortMessageId = 'permissionsShortMessage_workbooksAllWithInheritedViews';
      longMessageId = singleWorkbook ? 'permissionsLongMessage_workbooksSingleWithInheritedViews'
        : 'permissionsLongMessage_workbooksAllWithInheritedViews';
    } else {
      shortMessageId = 'permissionsShortMessage_workbooksAllWithIndependentViews';
      longMessageId = singleWorkbook ? 'permissionsLongMessage_workbooksSingleWithIndependentViews'
        : 'permissionsLongMessage_workbooksAllWithIndependentViews';
    }

    return [shortMessageId, longMessageId];
  }

  private messageIdsForReadOnly(): string[] {
    let shortMessageId, longMessageId;
    if (this.authorizables.size() === 1) {
      shortMessageId = 'permissionsShortMessage_locked';
      longMessageId = 'permissionsLongMessage_workbooksSingleInLockedProject';
    } else if (this.noAuthorizablesMatchSource()) {
      shortMessageId = 'permissionsShortMessage_locked';
      longMessageId = 'permissionsLongMessage_workbooksAllInLockedProjects';
    } else {
      shortMessageId = 'permissionsShortMessage_workbooksSomeInLockedProjects';
      longMessageId = 'permissionsLongMessage_workbooksSomeInLockedProjects';
    }

    return [shortMessageId, longMessageId];
  }

}


export class DatasourcePermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, DatasourceContentType.name, sourceAuthorizables, $translate);
  }

  protected getPromptMessage(): string {
    return this.editable ? null : Messages.lockPermissions_contentInLockedProject();
  }

  protected getPromptId(): string {
    return this.editable ? 'editDatasourcePermissionsDescription': 'readonlyDatasourcePermissionsDescription';
  }

  protected getMessages(): string[] {
    if (this.editable) {
      return ['', ''];
    }
    if (FeatureFlags.lockingSubProjectsEnabled) {
      if (this.noAuthorizablesMatchSource()) {
        return [Messages.lockPermissions_contentInLockedProject(), Messages.lockPermissions_contentInLockedProject()];
      }
      return [Messages.lockPermissions_contentInMixedProject(), Messages.lockPermissions_contentInMixedProject()];
    }

    let shortMessageId, longMessageId;
    if (this.noAuthorizablesMatchSource()) {
      shortMessageId = 'permissionsShortMessage_locked';
      longMessageId = 'permissionsLongMessage_datasourcesAllInLockedProjects';
    } else {
      shortMessageId = 'permissionsShortMessage_datasourcesSomeInLockedProjects';
      longMessageId = 'permissionsLongMessage_datasourcesSomeInLockedProjects';
    }

    const stringIds = [shortMessageId, longMessageId];
    return stringIds.map((stringId: string) => this.$translate.instant(
      stringId,
      { ITEM_COUNT: this.authorizables.size() },
      L10n.Interpolations.Count
    ));
  }

}

export class FlowPermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, FlowContentType.name, sourceAuthorizables, $translate);
  }

  protected getPromptMessage(): string {
    return this.editable ? null : Messages.lockPermissions_contentInLockedProject();
  }

  protected getPromptId(): string {
    return this.editable ? 'editFlowPermissionsDescription': 'readonlyFlowPermissionsDescription';
  }

  protected getMessages(): string[] {
    if (this.editable) {
      return ['', ''];
    }
    if (FeatureFlags.lockingSubProjectsEnabled) {
      if (this.noAuthorizablesMatchSource()) {
        return [Messages.lockPermissions_contentInLockedProject(), Messages.lockPermissions_contentInLockedProject()];
      }
      return [Messages.lockPermissions_contentInMixedProject(), Messages.lockPermissions_contentInMixedProject()];
    }

    let shortMessageId, longMessageId;
    if (this.noAuthorizablesMatchSource()) {
      shortMessageId = 'permissionsShortMessage_locked';
      longMessageId = 'permissionsLongMessage_flowsAllInLockedProjects';
    } else {
      shortMessageId = 'permissionsShortMessage_flowsSomeInLockedProjects';
      longMessageId = 'permissionsLongMessage_flowsSomeInLockedProjects';
    }

    const stringIds = [shortMessageId, longMessageId];
    return stringIds.map((stringId: string) => this.$translate.instant(
      stringId,
      { ITEM_COUNT: this.authorizables.size() },
      L10n.Interpolations.Count
    ));
  }

}

export class MetricPermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, MetricContentType.name, sourceAuthorizables, $translate);
  }

  protected getPromptMessage(): string {
    return this.editable ? null : Messages.lockPermissions_contentInLockedProject();
  }

  protected getPromptId(): string {
    return this.editable ? '' : 'lockPermissions_contentInLockedProject';
  }

  protected getMessages(): string[] {
    if (this.editable) {
      return ['', ''];
    }
    if (this.noAuthorizablesMatchSource()) {
      return [Messages.lockPermissions_contentInLockedProject(), Messages.lockPermissions_contentInLockedProject()];
    }
    return [Messages.lockPermissions_contentInMixedProject(), Messages.lockPermissions_contentInMixedProject()];
  }

}

export class PublishedConnectionPermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, PublishedConnectionContentType.name, sourceAuthorizables, $translate);
  }

  protected getPromptMessage(): string {
    return this.editable ? null : Messages.lockPermissions_contentInLockedProject();
  }

  protected getPromptId(): string {
    return this.editable ? '' : 'lockPermissions_contentInLockedProject';
  }

  protected getMessages(): string[] {
    if (this.editable) {
      return ['', ''];
    }
    if (this.noAuthorizablesMatchSource()) {
      return [Messages.lockPermissions_contentInLockedProject(), Messages.lockPermissions_contentInLockedProject()];
    }
    return [Messages.lockPermissions_contentInMixedProject(), Messages.lockPermissions_contentInMixedProject()];
  }

}

export class DatabasePermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {
  // Database is a top-level container where nesting not supported.
  // so it will always be editable and there is no locked/partially locked state for Database.

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, DatabaseContentType.name, sourceAuthorizables, $translate);
  }

  protected getLockState(): LockUnlockPermissionsState {
    const databases = this.authorizables as Set<IControlledPermissionItemInfo>;
    return LockStatusHelper.getLockState(databases);
  }

  protected getSectionHeaderMessageForLockState(lockState: LockUnlockPermissionsState): string | null {
    switch (lockState) {
      case LockUnlockPermissionsState.Locked:
        return Messages.lockDatabasePermissions_locked();
      case LockUnlockPermissionsState.Unlocked:
        return Messages.lockDatabasePermissions_unlocked();
      case LockUnlockPermissionsState.Mixed:
        return Messages.lockDatabasePermissions_mixed();
    }
  }

  protected getPromptId(): string {
    return 'editDatabasePermissionsDescription';
  }

  protected getMessages(): string[] {
    return ['', ''];
  }
}

export class TablePermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, TableContentType.name, sourceAuthorizables, $translate);
  }

  protected getPromptId(): string {
    return this.editable ? 'editTablePermissionsDescription': 'readonlyTablePermissionsDescription';
  }

  protected getMessages(): string[] {
    if (this.editable) {
      return ['', ''];
    }
    if (FeatureFlags.lockingSubProjectsEnabled) {
      if (this.noAuthorizablesMatchSource()) {
        return [Messages.lockPermissions_contentInLockedDatabase(), Messages.lockPermissions_contentInLockedDatabase()];
      }
      return [Messages.lockPermissions_contentInMixedDatabase(), Messages.lockPermissions_contentInMixedDatabase()];
    }

    let shortMessageId, longMessageId;
    if (this.noAuthorizablesMatchSource()) {
      shortMessageId = 'permissionsShortMessage_locked';
      longMessageId = 'permissionsLongMessage_tablesAllInLockedDatabases';
    } else {
      shortMessageId = 'permissionsShortMessage_tablesSomeInLockedDatabases';
      longMessageId = 'permissionsLongMessage_tablesSomeInLockedDatabases';
    }

    const stringIds = [shortMessageId, longMessageId];
    return stringIds.map((stringId: string) => this.$translate.instant(
      stringId,
      { ITEM_COUNT: this.authorizables.size() },
      L10n.Interpolations.Count
    ));
  }
}

export class DataRolePermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, DataRoleContentType.name, sourceAuthorizables, $translate);
  }

  protected getPromptMessage(): string {
    return this.editable ? null : Messages.lockPermissions_contentInLockedProject();
  }

  protected getPromptId(): string {
    return this.editable ? 'editDataRolePermissionsDescription': 'readonlyDataRolePermissionsDescription';
  }

  protected getMessages(): string[] {
    if (this.editable) {
      return ['', ''];
    }
    if (FeatureFlags.lockingSubProjectsEnabled) {
      if (this.noAuthorizablesMatchSource()) {
        return [Messages.lockPermissions_contentInLockedProject(), Messages.lockPermissions_contentInLockedProject()];
      }
      return [Messages.lockPermissions_contentInMixedProject(), Messages.lockPermissions_contentInMixedProject()];
    }

    let shortMessageId, longMessageId;
    if (this.noAuthorizablesMatchSource()) {
      shortMessageId = 'permissionsShortMessage_locked';
      longMessageId = 'permissionsLongMessage_dataRolesAllInLockedProjects';
    } else {
      shortMessageId = 'permissionsShortMessage_dataRolesSomeInLockedProjects';
      longMessageId = 'permissionsLongMessage_dataRolesSomeInLockedProjects';
    }

    const stringIds = [shortMessageId, longMessageId];
    return stringIds.map((stringId: string) => this.$translate.instant(
      stringId,
      { ITEM_COUNT: this.authorizables.size() },
      L10n.Interpolations.Count
    ));
  }

}


export class ViewPermissionsEditabilityLogic extends PermissionsEditabilityLogicBase {

  constructor(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>,
    $translate: ng.translate.ITranslateService
  ) {
    super(authorizables, ViewContentType.name, sourceAuthorizables, $translate);
  }

  protected getLockState(): LockUnlockPermissionsState {
    if (this.editable) {
      return LockUnlockPermissionsState.Unlocked;
    } else {
      const numSourcedFromProject = this.numSourcedFromProject();
      if (numSourcedFromProject > 0) {
        if (numSourcedFromProject < this.authorizables.size()) {
          return LockUnlockPermissionsState.Mixed;
        } else {
          return LockUnlockPermissionsState.Locked;
        }
      } else {
        return LockUnlockPermissionsState.Unlocked;
      }
    }
  }

  protected getPromptId(): string {
    return this.editable ? 'editViewPermissionsDescription': 'readonlyViewPermissionsDescription';
  }

  protected getMessages(): string[] {
    if (FeatureFlags.lockingSubProjectsEnabled) {
      if (this.editable) {
        return [Messages.lockPermissions_workbookViewsIndependentTabs(), Messages.lockPermissions_workbookViewsIndependentTabs()];
      }
      switch (this.getLockState()) {
        case LockUnlockPermissionsState.Locked:
          return [Messages.lockPermissions_contentInLockedProject(), Messages.lockPermissions_contentInLockedProject()];
        case LockUnlockPermissionsState.Unlocked:
          const numSourcedFromWorkbook = this.numSourcedFromWorkbook();
          if (numSourcedFromWorkbook > 0) {
            if (numSourcedFromWorkbook < this.authorizables.size()) {
              return [Messages.lockPermissions_workbookViewsMixedTabs(), Messages.lockPermissions_workbookViewsMixedTabs()];
            }
            return [Messages.lockPermissions_workbookViewsDisplayTabs(), Messages.lockPermissions_workbookViewsDisplayTabs()];
          }
          return [Messages.lockPermissions_workbookViewsIndependentTabs(), Messages.lockPermissions_workbookViewsIndependentTabs()];
        case LockUnlockPermissionsState.Mixed:
          return [Messages.lockPermissions_contentInMixedProject(), Messages.lockPermissions_contentInMixedProject()];
      }
    }
    if (this.editable) {
      return ['', ''];
    }

    // If any permissions are sourced from projects, we use the locked-to-the-project messaging.
    const numSourcedFromProject = this.numSourcedFromProject();

    const stringIds = (numSourcedFromProject > 0)
        ? this.messageIdsForLockedViews(numSourcedFromProject)
        : this.messageIdsForUnlockedViews();

    // SOURCE_NAME and SOURCE_COUNT are only referenced in the messages where all the sources are
    // workbooks, so we only count the workbook sources here.
    const sourceWorkbooks: ITypedIdWithName[] = _.uniq(_.filter(this.sourceAuthorizables,
        (source: ITypedIdWithName) => source.type === WorkbookContentType.name),
      (item: ITypedIdWithName) => item.type + item.id);

    return stringIds.map((stringId: string) => this.$translate.instant(
      stringId,
      {
        ITEM_COUNT: this.authorizables.size(),
        SOURCE_COUNT: sourceWorkbooks.length,
        SOURCE_NAME: L10n.elideIfLong(_.first(sourceWorkbooks) && _.first(sourceWorkbooks).name)
      },
      L10n.Interpolations.Count
    ));
  }

  private numSourcedFromProject(): number {
    return this.authorizables.map((authorizable: INamedItemInfo): number =>
        (this.sourceAuthorizables[authorizable.id].type === ProjectContentType.name) ? 1 : 0
    ).reduce((memo: number, next: number) => memo + next);
  }

  private numSourcedFromWorkbook(): number {
    return this.authorizables.map((authorizable: INamedItemInfo): number =>
        (this.sourceAuthorizables[authorizable.id].type === WorkbookContentType.name) ? 1 : 0
    ).reduce((memo: number, next: number) => memo + next);
  }
  private messageIdsForLockedViews(numSourcedFromProject: number) {
    let shortMessageId, longMessageId;
    if (numSourcedFromProject === this.authorizables.size()) {
      shortMessageId = 'permissionsShortMessage_locked';
      longMessageId = 'permissionsLongMessage_viewsAllInLockedProjects';
    } else {
      shortMessageId = 'permissionsShortMessage_viewsSomeInLockedProjects';
      longMessageId = 'permissionsLongMessage_viewsSomeInLockedProjects';
    }

    return [shortMessageId, longMessageId];
  }

  private  messageIdsForUnlockedViews() {
    let shortMessageId, longMessageId;
    if (this.authorizables.size() === 1) {
      shortMessageId = 'permissionsShortMessage_viewsAllInheritedFromWorkbooks';
      longMessageId = 'permissionsLongMessage_viewsSingleInheritedFromWorkbook';
    } else if (this.noAuthorizablesMatchSource()) {
      shortMessageId = 'permissionsShortMessage_viewsAllInheritedFromWorkbooks';
      longMessageId = 'permissionsLongMessage_viewsAllInheritedFromWorkbooks';
    } else {
      shortMessageId = 'permissionsShortMessage_viewsSomeInheritedFromWorkbooks';
      longMessageId = 'permissionsLongMessage_viewsSomeInheritedFromWorkbooks';
    }

    return [shortMessageId, longMessageId];
  }

}
